import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { useGetResumptionLeavesMutation } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/api/resumptionRequestApiSlice';
import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import { FooterActions } from '@Enum/index';
import { NotificationContext } from '@Context/NotificationContext';
import { parseDateTime } from '@Utils/transformDateTime';
import { OptionNum } from '@Components/types/options';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { getResumptionWorkShiftDict } from '@Store/selectors/dictionarySelector';
import { ResumptionLeavesResponse } from '@Types/api/form/resumptionRequest/ResumpationLeaves';
import { initialFormValues } from '../initialValues';

interface ResumptionRequestApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

const ResumptionRequestApproveForm: React.FC<
  ResumptionRequestApproveFormProps
> = ({ formData, taskId }) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();

  const [isReassignOpen, setReassignOpen] = useState(false);
  const [comments, setCommentValue] = useState('');
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const [respondResumptionRequest] = useRespondHrmRequestMutation();
  const [getResumptionLeaves] = useGetResumptionLeavesMutation();
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Resumption');
  const [resumingFromOptions, setResumingFromOptions] = useState<OptionNum[]>(
    []
  );
  const [resumptionLeaves, setResumptionLeaves] =
    useState<ResumptionLeavesResponse>();

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (formData) {
      formik.setValues({
        resumingFromLeaveRequestId: formData.leaveTypeID,
        resumingOnDate: parseDateTime(formData.startDate, 'DD/MM/YYYY'),
        workShift: formData.ticketRequired,
        currentLocation: formData.currentLocation,
        remarks: formData.remarks,
      });
    }
  }, [formData]);

  useEffect(() => {
    if (formData.empNumber) {
      getResumptionLeaves({
        empNumber: formData.empNumber,
        resumptionReqId: formData.requestId,
      })
        .unwrap()
        .then((res) => {
          setResumptionLeaves(res);
        });
    }
  }, [formData.empNumber]);

  useEffect(() => {
    if (resumptionLeaves?.ResDates) {
      const resumptionOptions = resumptionLeaves?.ResDates.map(
        (leave) =>
          ({
            label: leave.LeaveData,
            value: Number(leave.ReqID),
          }) as OptionNum
      );

      setResumingFromOptions(resumptionOptions);
    }
  }, [resumptionLeaves]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      const actionHandler = async (): Promise<void> => {
        try {
          await respondResumptionRequest(body).unwrap();

          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        } catch {
          taskErrorNotification();
        }
      };

      actionHandler();
    },
    [comments, taskId, handleBackToListRequests]
  );

  const dataSourceRequestDetails = useMemo(() => {
    const resumingFromDesc =
      resumingFromOptions.find(
        (item) => item.value === formik.values.resumingFromLeaveRequestId
      )?.label || '';
    const workShiftDesc =
      resumptionWorkShifts.find(
        (workShift) => workShift.codeNum === formik.values.workShift
      )?.codeDesc || '';

    return [
      {
        label: 'Resuming From (Selected Earlier Leave)',
        value: resumingFromDesc,
      },
      {
        label: 'Resuming on',
        value: formik.values.resumingOnDate,
      },
      {
        label: 'Work Shift',
        value: workShiftDesc,
      },
      {
        label: 'Current Location',
        value: formik.values.currentLocation || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Remarks',
        value: formik.values.remarks || EMPTY_FIELD_PLACEHOLDER,
      },
    ];
  }, [formik.values, resumingFromOptions]);

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        formType="approval"
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceRequestDetails}
            label="Request Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default ResumptionRequestApproveForm;
